import { ref, computed, Ref } from 'vue'
import axios from '@/api/axios'
import { useAuth } from './useAuth'

export interface Service {
    id: number
    name: string
    description?: string
    unit_id?: number
    is_default?: boolean
    icon?: string
}

export interface ServiceGroup {
    id: number
    name: string
    description?: string
    type: 'optional' | 'mandatory_choice' | 'exclusive_choice' | 'multi_required'
    min_selections?: number
    max_selections?: number
    validation_message?: string
    help_text?: string
    services: Service[]
    icon?: string
}

export interface ValidationError {
    group_id: number
    group_name: string
    message: string
}

// Pairwise BLOCK exclusion rule (DEV-219). Symmetric: if either side is
// selected/booked, the other must not be selectable.
export interface ServiceExclusion {
    service_id: number
    excluded_service_id: number
    message: string | null
}

// Icons for common service categories (fallback mapping)
export const SERVICE_GROUP_ICONS: Record<string, string> = {
    'kraftstoff': '⛽',
    'fuel': '⛽',
    'tanken': '⛽',
    'reinigung': '🧹',
    'cleaning': '🧹',
    'waschen': '🧹',
    'schäden': '⚠️',
    'schaden': '⚠️',
    'damage': '⚠️',
    'inspektion': '🔍',
    'inspection': '🔍',
    'prüfung': '🔍',
    'reifen': '⭕',
    'tires': '⭕',
    'wheels': '⭕',
    'dokumente': '📄',
    'documents': '📄',
    'papiere': '📄',
    'zubehör': '🔧',
    'accessories': '🔧',
    'extras': '🔧',
    'default': '📋'
}

export function getIconForGroup(groupName: string): string {
    const nameLower = groupName.toLowerCase()
    for (const [key, icon] of Object.entries(SERVICE_GROUP_ICONS)) {
        if (nameLower.includes(key)) {
            return icon
        }
    }
    return SERVICE_GROUP_ICONS.default
}

/**
 * How many members of the group a selection must contain.
 *
 * Mirrors ServiceGroup::requiredMinimum() on the server. `min_selections` is
 * never read below 1: the column is NOT NULL DEFAULT 0, and a `multi_required`
 * group that requires nothing is a misconfiguration, not a valid state.
 *
 * Two test tables with the same cases hold the mirror (here and in
 * ServiceGroupValidationTest). Nothing compares the two languages
 * automatically — the tables make a divergence obvious, not impossible.
 */
export function requiredMinimum(group: ServiceGroup): number {
    switch (group.type) {
        case 'mandatory_choice':
        case 'exclusive_choice':
            return 1
        case 'multi_required':
            return Math.max(1, group.min_selections || 0)
        default:
            return 0
    }
}

/**
 * Whether a selection satisfies this group — the ONE client-side implementation.
 * Module-level on purpose, so a component can use it without instantiating the
 * composable.
 *
 * `turnaroundReplaced` carries the DEV-240 exemption: a pickup ("FZG Abholung
 * Service-Center") REPLACES the group obligation, because the vehicle leaves us
 * and no Turnaround is owed — the billing review corrects "Abholung +
 * Turnaround" in 174 of 179 decided cases. It lifts the MINIMUM only; the
 * exclusive "at most one" half still holds.
 */
export function validateGroupLocally(
    group: ServiceGroup,
    selectedServiceIds: number[],
    turnaroundReplaced = false
): boolean {
    if (group.type === 'optional') return true

    const servicesInGroup = group.services.map((s: Service) => s.id)
    const count = selectedServiceIds.filter((id: number) => servicesInGroup.includes(id)).length

    if (group.type === 'exclusive_choice' && count > 1) return false

    return count >= (turnaroundReplaced ? 0 : requiredMinimum(group))
}

/**
 * Whether the selection contains a service that REPLACES the turnaround.
 *
 * `exemptServiceIds` comes from the API (`turnaround_exempt_service_ids`), which
 * derives it from the `pickup_service` setting — the same one the billing review
 * uses for its `pickup_plus_turnaround` rule. Deliberately NOT read from the
 * service catalogue's `hands_over_custody` flag: that flag means "the vehicle
 * leaves us, hand the key over automatically", which is a different question and
 * on production sits on a different service. Mirrors
 * ServiceGroupGuard::turnaroundExemptionApplies(), minus the role resolution.
 */
export function turnaroundIsReplaced(
    selectedServiceIds: number[],
    exemptServiceIds: number[],
    mayUseExemption: boolean
): boolean {
    if (!mayUseExemption) return false

    return selectedServiceIds.some(id => exemptServiceIds.includes(id))
}

/** A group that demands something. `optional` is the only type that does not. */
export function isMandatoryGroup(group: ServiceGroup): boolean {
    return group.type !== 'optional'
}

/**
 * The input both halves of the group check share: the selection that gets
 * validated, and the exemption flag derived from THAT SAME selection. Pure and
 * exported so the derivation is testable outside the view.
 *
 * `existingServiceIds` must already have the removal queue subtracted —
 * un-picking the PICKUP has to withdraw the exemption. (A custody service does
 * not grant it in the first place; that flag drives the key handover.)
 */
export function buildGroupCheckInput(
    existingServiceIds: number[],
    newlySelectedServiceIds: number[],
    exemptServiceIds: number[],
    mayUseExemption: boolean
): { selection: number[]; turnaroundReplaced: boolean } {
    const selection = [...new Set([...existingServiceIds, ...newlySelectedServiceIds])]

    return {
        selection,
        turnaroundReplaced: turnaroundIsReplaced(selection, exemptServiceIds, mayUseExemption),
    }
}

/**
 * Every group the selection fails, with the message to show for each.
 *
 * A group's own `validation_message` wins when configured — the backend has
 * always sent it and the client used to replace it with a hardcoded string
 * (TRK-45 point 4).
 */
export function collectGroupValidationErrors(
    groups: ServiceGroup[],
    selection: number[],
    turnaroundReplaced: boolean
): ValidationError[] {
    const errors: ValidationError[] = []

    for (const group of groups) {
        if (group.type === 'optional') continue
        if (validateGroupLocally(group, selection, turnaroundReplaced)) continue

        const memberIds = group.services.map(service => service.id)
        const count = selection.filter(id => memberIds.includes(id)).length

        errors.push({
            group_id: group.id,
            group_name: group.name,
            message: group.validation_message || defaultGroupMessage(group, count),
        })
    }

    return errors
}

function defaultGroupMessage(group: ServiceGroup, count: number): string {
    switch (group.type) {
        case 'mandatory_choice':
            return 'Bitte mindestens einen Service auswählen'
        case 'exclusive_choice':
            return count === 0
                ? 'Bitte genau einen Service auswählen'
                : `Bitte nur einen Service auswählen (${count} ausgewählt)`
        case 'multi_required':
            return `Bitte mindestens ${requiredMinimum(group)} Service(s) auswählen (${count} ausgewählt)`
        default:
            return `Ungültige Auswahl für ${group.name}`
    }
}

/**
 * Everything the group check produces, from one derivation.
 *
 * The submit button (progress), the tab ticks (validity) and the save gate
 * (errors) each used to call the rule themselves and pass the exemption flag as
 * their own argument. Three call sites that could silently forget it, and the
 * progress one is the harshest: it drives the button's `:disabled`, so a
 * forgotten flag locks a Disponent out of a check-in the handler would accept.
 * Deriving all three here leaves the view a single call.
 */
export function evaluateGroups(
    groups: ServiceGroup[],
    existingServiceIds: number[],
    newlySelectedServiceIds: number[],
    exemptServiceIds: number[],
    mayUseExemption: boolean
): {
    selection: number[]
    turnaroundReplaced: boolean
    validity: Record<number, boolean>
    errors: ValidationError[]
    progress: { completed: number; total: number; percentage: number }
} {
    const { selection, turnaroundReplaced } = buildGroupCheckInput(
        existingServiceIds,
        newlySelectedServiceIds,
        exemptServiceIds,
        mayUseExemption
    )

    const validity: Record<number, boolean> = {}
    for (const group of groups) {
        validity[group.id] = validateGroupLocally(group, selection, turnaroundReplaced)
    }

    const mandatory = groups.filter(isMandatoryGroup)
    const completed = mandatory.filter(group => validity[group.id]).length

    return {
        selection,
        turnaroundReplaced,
        validity,
        errors: collectGroupValidationErrors(groups, selection, turnaroundReplaced),
        progress: {
            completed,
            total: mandatory.length,
            percentage: mandatory.length === 0 ? 100 : Math.round((completed / mandatory.length) * 100),
        },
    }
}

export function useServiceGroups() {
    const serviceGroups: Ref<ServiceGroup[]> = ref([])
    const ungroupedServices: Ref<Service[]> = ref([])
    const recentlyUsedServices: Ref<Service[]> = ref([])
    const loading = ref(false)
    const error: Ref<string | null> = ref(null)
    const validationErrors: Ref<ValidationError[]> = ref([])
    const exclusions: Ref<ServiceExclusion[]> = ref([])
    /** Services that make a group minimum moot — from `pickup_service` (TRK-15). */
    const turnaroundExemptServiceIds: Ref<number[]> = ref([])
    /** Services whose booking hands the key over automatically. A DIFFERENT set. */
    const custodyHandoverServiceIds: Ref<number[]> = ref([])

    const fetchServiceGroups = async (rentalCompanyId?: number | null) => {
        loading.value = true
        error.value = null

        try {
            // Get the selected location to filter services by location type
            const { getSelectedLocation } = useAuth()
            const selectedLocation = getSelectedLocation()
            const locationType = selectedLocation?.type || null

            const response = await axios.get('/api/checker/service-groups', {
                params: {
                    ...(locationType ? { location_type: locationType } : {}),
                    // DEV-219: company-scoped exclusion rules; without it only
                    // global (all-company) rules are returned.
                    ...(rentalCompanyId != null ? { rental_company_id: rentalCompanyId } : {})
                }
            })
            serviceGroups.value = response.data.groups || []
            ungroupedServices.value = response.data.ungrouped_services || []
            recentlyUsedServices.value = response.data.recently_selected || []
            exclusions.value = response.data.exclusions || []
            turnaroundExemptServiceIds.value = response.data.turnaround_exempt_service_ids || []
            custodyHandoverServiceIds.value = response.data.custody_handover_service_ids || []
        } catch (err: any) {
            console.error('Failed to fetch service groups:', err)
            error.value = 'Fehler beim Laden der Service-Gruppen'
            serviceGroups.value = []
            ungroupedServices.value = []
            recentlyUsedServices.value = []
            exclusions.value = []
            turnaroundExemptServiceIds.value = []
            custodyHandoverServiceIds.value = []
        } finally {
            loading.value = false
        }
    }

    const validateSelection = async (selectedServiceIds: number[], existingServiceIds: number[] = []): Promise<boolean> => {
        try {
            // Get the selected location to filter validation by location type
            const { getSelectedLocation } = useAuth()
            const selectedLocation = getSelectedLocation()
            const locationType = selectedLocation?.type || null

            // Combine existing and newly selected services for validation
            const allServiceIds = [...new Set([...existingServiceIds, ...selectedServiceIds])]

            await axios.post('/api/checker/service-groups/validate', {
                service_ids: allServiceIds,
                existing_service_ids: existingServiceIds,
                location_type: locationType
            })
            validationErrors.value = []
            return true
        } catch (err: any) {
            if (err.response?.status === 422) {
                validationErrors.value = err.response.data.errors || []
                return false
            }
            console.error('Validation error:', err)
            return false
        }
    }

    const getMandatoryGroups = computed(() => serviceGroups.value.filter(isMandatoryGroup))

    const hasMandatoryGroups = computed(() => {
        return getMandatoryGroups.value.length > 0
    })

    // Get default services (marked as is_default in groups)
    const getDefaultServices = (): number[] => {
        const defaults: number[] = []
        for (const group of serviceGroups.value) {
            for (const service of group.services) {
                if (service.is_default) {
                    defaults.push(service.id)
                }
            }
        }
        return defaults
    }

    // Look up a service name across groups and ungrouped services (for messages)
    const findServiceName = (serviceId: number): string | null => {
        for (const group of serviceGroups.value) {
            const service = group.services.find(s => s.id === serviceId)
            if (service) return service.name
        }
        return ungroupedServices.value.find(s => s.id === serviceId)?.name || null
    }

    // First exclusion rule pairing serviceId with any active (selected or booked)
    // service, checking both directions of each symmetric rule. Null = no conflict.
    // DEV-219.
    const getExclusionConflict = (
        serviceId: number,
        activeServiceIds: number[]
    ): { withServiceId: number, message: string } | null => {
        for (const rule of exclusions.value) {
            let withServiceId: number | null = null
            if (rule.service_id === serviceId && activeServiceIds.includes(rule.excluded_service_id)) {
                withServiceId = rule.excluded_service_id
            } else if (rule.excluded_service_id === serviceId && activeServiceIds.includes(rule.service_id)) {
                withServiceId = rule.service_id
            }
            if (withServiceId === null) continue

            let message = rule.message
            if (!message) {
                const nameA = findServiceName(serviceId)
                const nameB = findServiceName(withServiceId)
                message = nameA && nameB
                    ? `„${nameA}“ und „${nameB}“ schließen sich aus.`
                    : 'Diese Services schließen sich aus.'
            }
            return { withServiceId, message }
        }
        return null
    }

    // Get all mandatory services (one from each mandatory group)
    const getMandatoryDefaults = (): number[] => {
        const defaults: number[] = []
        for (const group of getMandatoryGroups.value) {
            // Pick the first service or the default one
            const defaultService = group.services.find(s => s.is_default) || group.services[0]
            if (defaultService) {
                defaults.push(defaultService.id)
            }
        }
        return defaults
    }

    return {
        serviceGroups,
        ungroupedServices,
        recentlyUsedServices,
        loading,
        error,
        validationErrors,
        exclusions,
        turnaroundExemptServiceIds,
        custodyHandoverServiceIds,
        getExclusionConflict,
        fetchServiceGroups,
        validateSelection,
        validateGroupLocally,
        getMandatoryGroups,
        hasMandatoryGroups,
        getDefaultServices,
        getMandatoryDefaults,
        getIconForGroup
    }
}
